/**
 * Desktop login (lane D1; INVENTORY-DESKTOP §3.1, prototype template lines
 * 29-57).
 *
 * Rendered by `DesktopApp` with NO props and WITHOUT the top nav
 * (`headerVM().isLogin`). Owns no auth logic of its own: `doLogin()` routes
 * temp-password accounts to `screen:'reset'` itself, exactly as on mobile.
 *
 * The view model is mobile's — `loginVM(s)` — so the two trees can never drift
 * on which tiles exist, which one is selected, or what the error says. Only the
 * geometry is desktop: a fixed 520px card instead of mobile's fluid column, a
 * 15px wordmark instead of 13px, and the `WHAT ARE WE PLAYING?` tagline, which
 * mobile's own port already carries.
 *
 * DEVIATIONS
 *  - D-01 (inherited): seeded passwords are `START123`, so the dashed footnote
 *    advertises `START123` and not the prototype's `START`.
 *  - The footnote itself renders only in a DEV build against `LocalRepository`
 *    (`showDevLogins()`), the same gate mobile uses — in Supabase mode there is
 *    no shared password to advertise.
 *  - D1-01: `width:520px` is the prototype's literal and is content-box (no
 *    global `box-sizing` reset exists in either tree), so the card measures
 *    586px border-box. Faithful; see docs/porting/D1.md.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../../components/PixelButton.tsx';
import { QuarterIcon } from '../../components/QuarterIcon.tsx';
import { useQd } from '../../store/index.ts';
import { showDevLogins } from '../../store/selectors.3b.ts';
import { loginVM } from '../../store/selectors.ts';

/** §3.2 shares this shell verbatim; kept local to each screen to stay lane-safe. */
const SHELL: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const FIELD: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '13px',
  padding: '11px 12px',
  outline: 'none',
};

export default function Login(): JSX.Element {
  const s = useQd();
  const v = loginVM(s);

  return (
    <div data-screen-label="Login" style={SHELL}>
      <div
        style={{
          width: '520px',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: 'var(--gt-shadow-panel)',
          padding: '34px 32px 26px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <QuarterIcon u={2} hover={false} />
          <div
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: '15px',
              color: 'var(--gt-text)',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            GAMES TONIGHT<span style={{ color: 'var(--acc2)' }}>?</span>
          </div>
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '10.5px',
              letterSpacing: '.14em',
              color: 'var(--gt-muted)',
            }}
          >
            WHAT ARE WE PLAYING?
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* `EMAIL`, the mirrors' own label, true since the seeding on
              2026-08-21 — see the phone screen for what made it true, and for
              why a player name still works without being advertised. */}
          <input
            value={s.sel}
            onChange={(e) => s.setSel(e.target.value)}
            onKeyDown={(e) => {
              // `e.repeat` filters a HELD Enter, which auto-repeats keydown.
              // `doLogin` drops the extras anyway; this stops them being made.
              if (e.key === 'Enter' && !e.repeat) void s.doLogin();
            }}
            placeholder="EMAIL"
            style={FIELD}
          />
          <input
            type="password"
            value={v.loginPw}
            onChange={(e) => s.setLoginPw(e.target.value)}
            onKeyDown={(e) => {
              // `e.repeat` filters a HELD Enter, which auto-repeats keydown.
              // `doLogin` drops the extras anyway; this stops them being made.
              if (e.key === 'Enter' && !e.repeat) void s.doLogin();
            }}
            placeholder="PASSWORD"
            style={FIELD}
          />
          {v.hasLoginErr ? (
            <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '11px', color: 'var(--acc2)' }}>
              ✕ <span>{v.loginErr}</span>
            </div>
          ) : null}
          <PixelButton
            label={v.loginLbl}
            busy={v.loginBusy}
            onClick={() => void s.doLogin()}
            fs={9}
            pad="14px 16px"
            center
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9.5px',
              letterSpacing: '.04em',
            }}
          >
            {/* Real since 2026-08-21 — see the phone twin for what unblocked
                it, and why it stopped being optional. */}
            <span
              onClick={s.openForgot}
              style={{ color: 'var(--acc)', cursor: 'pointer', padding: '6px 0' }}
            >
              FORGOT PASSWORD?
            </span>
            <div style={{ flex: 1 }} />
            <span
              onClick={s.openWizard}
              style={{ color: 'var(--acc)', cursor: 'pointer', padding: '6px 0' }}
            >
              START A PARTY
            </span>
          </div>
        </div>

        {showDevLogins() ? (
          <div
            style={{
              border: '1px dashed var(--gt-border-2)',
              padding: '10px 12px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '10px',
              lineHeight: 1.8,
              color: 'var(--gt-muted)',
            }}
          >
            PROTOTYPE LOGINS — password for everyone: <span style={{ color: 'var(--gt-text-3)' }}>START123</span>
            <br />
            CHELSEA has a temp password (first-login reset flow) · MARCUS is the admin
          </div>
        ) : null}
      </div>
    </div>
  );
}
